<template>
  <div class="detail-page">
    <!-- Header with Back Button - Fixed -->
    <div class="detail-header detail-header-fixed">
      <a-button type="text" class="back-button" @click="handleBack">
        <ArrowLeft :size="16" class="back-icon" />
        返回列表
      </a-button>
      <div class="header-actions">
        <div v-if="dataSource" class="data-source-badge">
          <Database :size="14" class="badge-icon" />
          {{ dataSource }}
        </div>
      </div>
    </div>

    <!-- Content Area - Scrollable -->
    <div class="detail-content">
      <!-- Title Section -->
      <div class="title-section">
        <div class="title-row">
          <h1 class="title-text">{{ title }}</h1>
          <a-button
            v-if="Number(labelGroup) === 4 && data.project_title"
            type="text"
            size="small"
            class="translate-button"
            :loading="fieldTranslationLoading.project_title"
            @click="toggleFieldTranslation('project_title')"
          >
            {{ fieldShowTranslated.project_title ? '查看原文' : '翻译标题' }}
          </a-button>
        </div>
      </div>

      <!-- Basic Information -->
    <a-card v-if="basicInfo.length > 0" id="basic-info" class="info-card">
      <template #title>
        <div class="card-title">基本信息</div>
      </template>
      <div class="info-content">
        <div class="info-grid">
          <div v-for="(item, index) in basicInfo" :key="index" class="info-item">
            <div class="info-label">{{ item.label }}:</div>
            <div class="info-value">
              <a 
                v-if="isLinkField(item.key) && item.value && item.value !== '-'" 
                :href="item.value" 
                target="_blank"
                rel="noopener noreferrer"
                class="link-cell"
              >
                {{ item.value }}
              </a>
              <span v-else-if="isListType(item.key)">
                <template v-if="getListItems(item.value).length > 0">
                  <span v-for="(listItem, listIndex) in getListItems(item.value)" :key="listIndex">
                    {{ listItem }}<span v-if="listIndex < getListItems(item.value).length - 1">, </span>
                  </span>
                </template>
                <span v-else>-</span>
              </span>
              <span v-else>{{ item.value }}</span>
              <a-button
                v-if="Number(labelGroup) === 4 && item.key === 'paper_abstructs' && item.value && item.value !== '-'"
                type="link"
                size="small"
                class="inline-translate-button"
                :loading="fieldTranslationLoading.paper_abstructs"
                @click="toggleFieldTranslation('paper_abstructs')"
              >
                {{ fieldShowTranslated.paper_abstructs ? '查看原文' : '翻译摘要' }}
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </a-card>

    <!-- Collection Fields -->
    <a-card v-if="collectionFields.length > 0" id="collection-info" class="info-card">
      <template #title>
        <div class="card-title">采集信息</div>
      </template>
      <div class="collection-content">
        <div v-for="(field, index) in collectionFields" :key="index" class="collection-item">
          <div class="collection-dot"></div>
          <span class="collection-label">{{ field.label }} : </span>
          <span class="collection-value">{{ field.value }}</span>
        </div>
      </div>
    </a-card>

    <!-- Source Content -->
    <a-card id="source-content" class="info-card source-content-card">
      <template #title>
        <div class="card-title-wrapper">
          <div class="card-title">原文</div>
          <div class="card-actions" v-if="sourceContent">
            <a-button
              type="text"
              size="small"
              class="translate-button"
              :loading="isTranslating"
              @click="handleTranslate"
            >
              {{ showTranslated ? '查看原文' : '翻译原文' }}
            </a-button>
            <a-button 
              type="text" 
              size="small" 
              class="fullscreen-button"
              @click="enterFullscreen"
            >
              <Maximize2 :size="16" />
              全屏阅读
            </a-button>
          </div>
        </div>
      </template>
      <div v-if="translationError" class="translate-error">
        {{ translationError }}
      </div>
      <div v-if="sourceContent" class="source-content-wrapper">
        <template v-if="showTranslated && translatedContent">
          <div v-if="isMarkdown" class="markdown-content" v-html="renderedTranslatedMarkdown"></div>
          <pre v-else class="source-content-text">{{ translatedContent }}</pre>
        </template>
        <template v-else>
          <div v-if="isMarkdown" class="markdown-content" v-html="renderedMarkdown"></div>
          <pre v-else class="source-content-text">{{ formattedSourceContent }}</pre>
        </template>
      </div>
      <div v-else class="source-content-empty">
        <div class="empty-text">暂无原文内容</div>
      </div>
    </a-card>

    <!-- Fullscreen View -->
    <div v-if="isFullscreen" class="fullscreen-container">
      <div class="fullscreen-header">
        <div class="fullscreen-title">
          <span>{{ showTranslated && translatedContent ? '译文 - 全屏阅读' : '原文 - 全屏阅读' }}</span>
          <div class="fullscreen-actions">
            <a-button
              v-if="sourceContent"
              type="text"
              size="small"
              class="translate-button"
              :loading="isTranslating"
              @click="handleTranslate"
            >
              {{ showTranslated ? '查看原文' : '翻译全文' }}
            </a-button>
            <a-button 
              type="text" 
              size="small" 
              @click="exitFullscreen"
              class="exit-fullscreen-button"
            >
              <Minimize2 :size="16" />
              退出全屏
            </a-button>
          </div>
        </div>
      </div>
      <div class="fullscreen-content-wrapper">
        <template v-if="showTranslated && translatedContent">
          <div v-if="isMarkdown" class="markdown-content" v-html="renderedTranslatedMarkdown"></div>
          <pre v-else class="fullscreen-content-text">{{ translatedContent }}</pre>
        </template>
        <template v-else>
          <div v-if="isMarkdown" class="markdown-content" v-html="renderedMarkdown"></div>
          <pre v-else class="fullscreen-content-text">{{ formattedSourceContent }}</pre>
        </template>
      </div>
    </div>
    </div>

    <!-- Floating Anchor Points -->
    <div v-if="showAnchors" class="floating-anchors">
      <div
        v-if="!isBasicInfoVisible"
        class="anchor-item"
        @click="scrollToSection('basic-info')"
      >
        <div class="anchor-dot"></div>
        <span class="anchor-label">基本信息</span>
      </div>
      <div
        v-if="!isCollectionInfoVisible && collectionFields.length > 0"
        class="anchor-item"
        @click="scrollToSection('collection-info')"
      >
        <div class="anchor-dot"></div>
        <span class="anchor-label">采集信息</span>
      </div>
      <div
        v-if="!isSourceContentVisible"
        class="anchor-item"
        @click="scrollToSection('source-content')"
      >
        <div class="anchor-dot"></div>
        <span class="anchor-label">原文</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ArrowLeft, Database, Maximize2, Minimize2 } from 'lucide-vue-next'
import { getDetailColumns } from '../../utils/columnConfig'
import { marked } from 'marked'
import { isMobileDevice } from '../../utils/deviceDetection'

export default {
  name: 'DetailPage',
  components: {
    ArrowLeft,
    Database,
    Maximize2,
    Minimize2
  },
  props: {
    labelGroup: {
      type: [String, Number],
      required: true
    },
    id: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      data: {},
      columns: [],
      spiderFields: [], // 接口返回的采集信息字段
      dataSourceName: '', // 接口返回的数据源名称
      isBasicInfoVisible: true,
      isCollectionInfoVisible: true,
      isSourceContentVisible: true,
      isFullscreen: false,
      isMobile: false,
      isTranslating: false,
      translatedContent: '',
      showTranslated: false,
      translationError: '',
      translatedFieldValues: {
        project_title: '',
        paper_abstructs: ''
      },
      fieldShowTranslated: {
        project_title: false,
        paper_abstructs: false
      },
      fieldTranslationLoading: {
        project_title: false,
        paper_abstructs: false
      }
    }
  },
  computed: {
    title() {
      const labelGroupNum = Number(this.labelGroup)
      const titleFieldMap = {
        1: 'description',
        2: 'policies_title',
        3: 'bid_title',
        4: 'project_title',
        5: 'product_name',
        6: 'project_title'
      }
      const titleField = titleFieldMap[labelGroupNum] || 'title'
      if (labelGroupNum === 4 && titleField === 'project_title' && this.fieldShowTranslated.project_title && this.translatedFieldValues.project_title) {
        return this.translatedFieldValues.project_title
      }
      return this.data[titleField] || '详情'
    },
    basicInfo() {
      const labelGroupNum = Number(this.labelGroup)
      // 详情页显示所有列，包括 visible: false 的列（列表页不显示但详情页显示）
      return this.columns
        .filter(col => col.key !== 'actions')
        .map(col => ({
          key: col.key,
          label: col.label,
          value: (
            labelGroupNum === 4 &&
            ((col.key === 'project_title' && this.fieldShowTranslated.project_title) ||
              (col.key === 'paper_abstructs' && this.fieldShowTranslated.paper_abstructs)) &&
            this.translatedFieldValues[col.key]
          ) ? this.translatedFieldValues[col.key] : (this.data[col.key] || '-'),
          type: col.type // 保留列的 type 信息，用于判断是否为 list 类型
        }))
    },
    collectionFields() {
      // 优先使用接口返回的采集信息字段
      if (this.spiderFields && this.spiderFields.length > 0) {
        return this.spiderFields
      }
      // 如果没有接口数据，返回空数组（不再使用写死的默认值）
      return []
    },
    dataSource() {
      // 优先使用接口返回的数据源名称
      if (this.dataSourceName) {
        return this.dataSourceName
      }
      // 如果没有接口数据，返回空字符串
      return ''
    },
    sourceContent() {
      const content = this.data.source_content
      // 只有当 source_content 存在且不为空字符串时才返回
      return content && content.trim() ? content : ''
    },
    // 检查是否为 markdown 格式
    isMarkdown() {
      return this.data.is_markdown === true || this.data.is_markdown === 'true' || this.data.is_markdown === 1
      // return true
    },
    // 渲染 markdown 为 HTML
    renderedMarkdown() {
      if (!this.isMarkdown || !this.sourceContent) {
        return ''
      }
      try {
        return marked(this.sourceContent)
      } catch (error) {
        console.error('Markdown 解析失败:', error)
        return this.sourceContent
      }
    },
    renderedTranslatedMarkdown() {
      if (!this.isMarkdown || !this.translatedContent) {
        return ''
      }
      try {
        return marked(this.normalizeTranslatedMarkdown(this.translatedContent))
      } catch (error) {
        console.error('译文 Markdown 解析失败:', error)
        return this.translatedContent
      }
    },
    formattedSourceContent() {
      const content = this.sourceContent
      if (!content) {
        return ''
      }
      
      // 尝试判断是否为 JSON 字符串
      try {
        // 如果以 { 或 [ 开头，尝试解析为 JSON
        const trimmed = content.trim()
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || 
            (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          const parsed = JSON.parse(content)
          
          // 递归处理对象，将包含 "；\n" 的字符串值转换为数组
          const processValue = (obj) => {
            if (typeof obj === 'string') {
              // 如果字符串包含 "；\n"，将其 split 成数组
              if (obj.includes('；\\n') || obj.includes('；\n')) {
                const separator = obj.includes('；\\n') ? '；\\n' : '；\n'
                const parts = obj.split(separator).filter(part => part.trim())
                return parts.length > 1 ? parts : obj
              }
              return obj
            }
            
            if (Array.isArray(obj)) {
              return obj.map(item => processValue(item))
            }
            
            if (obj !== null && typeof obj === 'object') {
              const result = {}
              for (const key in obj) {
                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                  result[key] = processValue(obj[key])
                }
              }
              return result
            }
            
            return obj
          }
          
          const processed = processValue(parsed)
          
          // 格式化 JSON，使用 2 个空格缩进
          let formatted = JSON.stringify(processed, null, 2)
          
          // 处理字符串值中的 \\n，将其转换为真正的换行
          // 使用状态机，只替换字符串值内部的 \\n，保持 JSON 结构
          let result = ''
          let inString = false
          let escapeNext = false
          
          for (let i = 0; i < formatted.length; i++) {
            const char = formatted[i]
            
            if (escapeNext) {
              if (char === 'n' && inString) {
                // 在字符串内部遇到 \n，转换为真正的换行
                result += '\n'
              } else {
                result += '\\' + char
              }
              escapeNext = false
              continue
            }
            
            if (char === '\\') {
              escapeNext = true
              continue
            }
            
            if (char === '"') {
              inString = !inString
            }
            
            result += char
          }
          
          return result
        }
      } catch (e) {
        // 解析失败，不是有效的 JSON，返回原内容
      }
      
      // 不是 JSON 或解析失败，返回原内容（处理 \n）
      return content.replace(/\\n/g, '\n')
    },
    showAnchors() {
      return (!this.isBasicInfoVisible) || 
             (!this.isCollectionInfoVisible && this.collectionFields.length > 0) ||
             !this.isSourceContentVisible
    }
  },
  watch: {
    isFullscreen(newVal) {
      this.$nextTick(() => {
        if (newVal) {
          // Modal 打开时，阻止 body 滚动
          document.body.classList.add('fullscreen-open')
          document.documentElement.classList.add('fullscreen-open')
          document.body.style.overflow = 'hidden'
          document.documentElement.style.overflow = 'hidden'
        } else {
          // Modal 关闭时，恢复 body 滚动
          document.body.classList.remove('fullscreen-open')
          document.documentElement.classList.remove('fullscreen-open')
          document.body.style.overflow = ''
          document.documentElement.style.overflow = ''
        }
      })
    }
  },
  created() {
    this.loadData()
  },
  mounted() {
    this.checkMobile()
    this.checkSectionVisibility()
    // 监听 detail-content 容器的滚动事件
    this.$nextTick(() => {
      const scrollContainer = document.querySelector('.detail-content')
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', this.handleScroll)
      }
    })
    window.addEventListener('resize', this.checkMobile)
    window.addEventListener('resize', this.checkSectionVisibility)
  },
  beforeUnmount() {
    const scrollContainer = document.querySelector('.detail-content')
    if (scrollContainer) {
      scrollContainer.removeEventListener('scroll', this.handleScroll)
    }
    window.removeEventListener('resize', this.checkMobile)
    window.removeEventListener('resize', this.checkSectionVisibility)
    // 确保退出时恢复 body 滚动
    document.body.style.overflow = ''
  },
  methods: {
    detectLangPair(text) {
      const hasChinese = /[\u4e00-\u9fff]/.test(text)
      return hasChinese
        ? { sourceLang: 'zh-CN', targetLang: 'en' }
        : { sourceLang: 'en', targetLang: 'zh-CN' }
    },
    splitTextForTranslate(text, maxLen = 450) {
      const cleanText = String(text || '')
      if (!cleanText) return []
      if (cleanText.length <= maxLen) return [cleanText]
      const chunks = []
      let start = 0
      while (start < cleanText.length) {
        let end = Math.min(start + maxLen, cleanText.length)
        if (end < cleanText.length) {
          const slice = cleanText.slice(start, end)
          const breakIndex = Math.max(
            slice.lastIndexOf('\n'),
            slice.lastIndexOf('。'),
            slice.lastIndexOf('！'),
            slice.lastIndexOf('？'),
            slice.lastIndexOf('. '),
            slice.lastIndexOf(';'),
            slice.lastIndexOf('；')
          )
          if (breakIndex > 80) {
            end = start + breakIndex + 1
          }
        }
        chunks.push(cleanText.slice(start, end))
        start = end
      }
      return chunks
    },
    async translateChunk(chunk, sourceLang, targetLang) {
      const url = `https://api.mymemory.translated.net/get?q=${encodeURIComponent(chunk)}&langpair=${encodeURIComponent(sourceLang)}|${encodeURIComponent(targetLang)}`
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error('翻译服务不可用')
      }
      const result = await response.json()
      const responseStatus = Number(result?.responseStatus)
      const responseDetails = String(result?.responseDetails || '')
      if (responseStatus && responseStatus !== 200) {
        throw new Error(responseDetails || '翻译服务返回异常')
      }
      const text = result?.responseData?.translatedText
      if (!text || /invalid source language/i.test(text)) {
        throw new Error('翻译结果为空')
      }
      return text
    },
    async translateTextInChunks(text, sourceLang, targetLang, maxLen = 450) {
      const chunks = this.splitTextForTranslate(text, maxLen)
      if (chunks.length === 0) return ''
      const translatedChunks = []
      for (const chunk of chunks) {
        const translated = await this.translateChunk(chunk, sourceLang, targetLang)
        translatedChunks.push(translated)
      }
      return translatedChunks.join('')
    },
    async translateFrontierTechFields(sourceLang, targetLang) {
      if (Number(this.labelGroup) !== 4) return
      const fieldsToTranslate = ['project_title', 'paper_abstructs']
      for (const fieldKey of fieldsToTranslate) {
        const rawValue = String(this.data[fieldKey] || '').trim()
        if (!rawValue || rawValue === '-') {
          this.translatedFieldValues[fieldKey] = ''
          continue
        }
        if (this.translatedFieldValues[fieldKey]) {
          continue
        }
        this.translatedFieldValues[fieldKey] = await this.translateTextInChunks(rawValue, sourceLang, targetLang)
      }
    },
    async toggleFieldTranslation(fieldKey) {
      if (Number(this.labelGroup) !== 4) return
      const rawValue = String(this.data[fieldKey] || '').trim()
      if (!rawValue || rawValue === '-') return

      if (this.translatedFieldValues[fieldKey]) {
        this.fieldShowTranslated[fieldKey] = !this.fieldShowTranslated[fieldKey]
        return
      }

      this.fieldTranslationLoading[fieldKey] = true
      try {
        const { sourceLang, targetLang } = this.detectLangPair(rawValue)
        this.translatedFieldValues[fieldKey] = await this.translateTextInChunks(rawValue, sourceLang, targetLang)
        this.fieldShowTranslated[fieldKey] = true
      } catch (error) {
        console.error(`${fieldKey} 翻译失败:`, error)
      } finally {
        this.fieldTranslationLoading[fieldKey] = false
      }
    },
    async translateMarkdownPreservingSyntax(markdown, sourceLang, targetLang) {
      const lines = String(markdown || '').split('\n')
      const cache = new Map()
      let inCodeBlock = false
      const output = []

      const translateTextWithCache = async (text) => {
        const clean = String(text || '')
        if (!clean.trim()) return clean
        if (!/[A-Za-z\u4e00-\u9fff]/.test(clean)) return clean
        if (cache.has(clean)) return cache.get(clean)
        const translated = await this.translateTextInChunks(clean, sourceLang, targetLang)
        cache.set(clean, translated)
        return translated
      }

      for (const line of lines) {
        if (/^\s*```/.test(line)) {
          inCodeBlock = !inCodeBlock
          output.push(line)
          continue
        }
        if (inCodeBlock || !line.trim()) {
          output.push(line)
          continue
        }

        const prefixMatch = line.match(/^(\s*(?:[-*+]\s+|\d+\.\s+|#{1,6}\s+|>\s+)*)/)
        const prefix = prefixMatch ? prefixMatch[1] : ''
        const body = line.slice(prefix.length)

        // 保护链接和行内代码，避免翻译后破坏 markdown 结构
        const placeholders = []
        const protectedBody = body.replace(/`[^`]*`|!?\[[^\]]*\]\([^)]+\)/g, (m) => {
          const token = `[[[MDTOKEN${placeholders.length}]]]`
          placeholders.push(m)
          return token
        })

        const translatedBody = await translateTextWithCache(protectedBody)
        let restoredBody = translatedBody
        restoredBody = restoredBody.replace(/\[\[\[\s*MDTOKEN\s*(\d+)\s*\]\]\]/gi, (_, idx) => placeholders[Number(idx)] || '')
        restoredBody = restoredBody.replace(/_*\s*MD[_\s-]*TOKEN[_\s-]*(\d+)\s*_*/gi, (_, idx) => placeholders[Number(idx)] || '')
        restoredBody = this.normalizeTranslatedMarkdown(restoredBody)
        output.push(`${prefix}${restoredBody}`)
      }

      return output.join('\n')
    },
    normalizeTranslatedMarkdown(text) {
      let normalized = String(text || '')
      if (!normalized) return normalized

      // 翻译服务常会把 markdown 标记转义成 \*、\# 等，恢复为可解析语法
      normalized = normalized
        .replace(/\\([*_`#[\]()!>-])/g, '$1')
        .replace(/\r\n/g, '\n')

      // 修复标题语法：#标题 -> # 标题
      normalized = normalized.replace(/^(#{1,6})([^\s#])/gm, '$1 $2')

      // 尽量保持列表项语法，避免 "* * 作者" 这类写法无法被解析
      normalized = normalized.replace(/^\*\s+\*\s+/gm, '- ')
      normalized = normalized.replace(/^[•·]\s+/gm, '- ')
      normalized = normalized
        .replace(/\*\*\s*([^*\n]+?)\s*\*\*/g, '$1')
        .replace(/\*\s*([^*\n]+?)\s*\*/g, '$1')
        .replace(/^\s*-\s*\*\s+/gm, '- ')

      return normalized
    },
    async handleTranslate() {
      if (!this.sourceContent) return
      if (this.showTranslated && this.translatedContent) {
        this.showTranslated = false
        return
      }
      if (this.translatedContent) {
        this.showTranslated = true
        return
      }
      this.isTranslating = true
      this.translationError = ''
      try {
        const { sourceLang, targetLang } = this.detectLangPair(this.sourceContent)
        await this.translateFrontierTechFields(sourceLang, targetLang)
        if (this.isMarkdown) {
          this.translatedContent = await this.translateMarkdownPreservingSyntax(this.sourceContent, sourceLang, targetLang)
        } else {
          this.translatedContent = await this.translateTextInChunks(this.sourceContent, sourceLang, targetLang)
        }
        this.showTranslated = true
      } catch (error) {
        console.error('翻译失败:', error)
        this.translationError = '翻译失败，请稍后重试'
      } finally {
        this.isTranslating = false
      }
    },
    async loadData() {
      const labelGroupNum = Number(this.labelGroup)
      
      // 根据 labelGroup 调用对应的详情接口
      if (labelGroupNum === 1) {
        // 市场端详情接口
        try {
          const { getMarketDetail } = await import('@/api/filterApi')
          const response = await getMarketDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            this.data = response.data?.info || {}
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载市场端详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else if (labelGroupNum === 2) {
        // 国家政策详情接口
        try {
          const { getPoliciesDetail } = await import('@/api/filterApi')
          const response = await getPoliciesDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            this.data = response.data?.info || {}
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载国家政策详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else if (labelGroupNum === 3) {
        // 招投标信息详情接口
        try {
          const { getBiddingDetail } = await import('@/api/filterApi')
          const response = await getBiddingDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            this.data = response.data?.info || {}
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载招投标信息详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else if (labelGroupNum === 4) {
        // 前沿技术详情接口
        try {
          const { getNewTechDetail } = await import('@/api/filterApi')
          const response = await getNewTechDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            this.data = response.data?.info || {}
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载前沿技术详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else if (labelGroupNum === 5) {
        // 器械注册详情接口
        try {
          const { getDeviceDetail } = await import('@/api/filterApi')
          const response = await getDeviceDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            this.data = response.data?.info || {}
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载器械注册详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else if (labelGroupNum === 6) {
        // 临床研究详情接口
        try {
          const { getClinicalDetail } = await import('@/api/filterApi')
          const response = await getClinicalDetail(this.id)
          if (response.errno === 0) {
            // 接口返回的数据结构为 { data: { info: {...}, spider_fields: [...], data_source_name: ... } }
            const info = response.data?.info || {}
            // 处理临床研究数据，解析 source_data 中的 studyType 和 phase
            if (!info.study_type || !info.phase) {
              try {
                const sourceData = typeof info.source_data === 'string' 
                  ? JSON.parse(info.source_data) 
                  : info.source_data
                if (sourceData?.metadata) {
                  info.study_type = info.study_type || sourceData.metadata.studyType || ''
                  info.phase = info.phase || sourceData.metadata.phase || ''
                }
              } catch (e) {
                // 解析失败，使用默认值
                info.study_type = info.study_type || ''
                info.phase = info.phase || ''
              }
            }
            this.data = info
            this.spiderFields = response.data?.spider_fields || []
            this.dataSourceName = response.data?.data_source_name || ''
          }
        } catch (error) {
          console.error('加载临床研究详情失败:', error)
          this.data = {}
          this.spiderFields = []
          this.dataSourceName = ''
        }
      } else {
        // 其他类目暂时没有详情接口，显示空数据
        this.data = {}
        this.spiderFields = []
        this.dataSourceName = ''
      }
      
      // 根据 labelGroup 设置列配置（使用共享配置）
      this.columns = getDetailColumns(labelGroupNum)
      this.translatedContent = ''
      this.showTranslated = false
      this.translationError = ''
      this.translatedFieldValues.project_title = ''
      this.translatedFieldValues.paper_abstructs = ''
      this.fieldShowTranslated.project_title = false
      this.fieldShowTranslated.paper_abstructs = false
      
      // 数据加载完成后检查各模块可见性
      this.$nextTick(() => {
        this.checkSectionVisibility()
      })
    },
    handleBack() {
      this.$router.push({
        name: 'Dashboard',
        params: { labelGroup: this.labelGroup }
      })
    },
    enterFullscreen() {
      this.isFullscreen = true
      // 阻止 body 滚动
      this.$nextTick(() => {
        document.body.classList.add('fullscreen-open')
        document.documentElement.classList.add('fullscreen-open')
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
      })
    },
    exitFullscreen() {
      this.isFullscreen = false
      // 恢复 body 滚动
      this.$nextTick(() => {
        document.body.classList.remove('fullscreen-open')
        document.documentElement.classList.remove('fullscreen-open')
        document.body.style.overflow = ''
        document.documentElement.style.overflow = ''
      })
    },
    checkSectionVisibility() {
      this.$nextTick(() => {
        const scrollContainer = document.querySelector('.detail-content')
        if (!scrollContainer) return
        
        const containerRect = scrollContainer.getBoundingClientRect()
        const basicInfoEl = document.getElementById('basic-info')
        const collectionInfoEl = document.getElementById('collection-info')
        const sourceContentEl = document.getElementById('source-content')
        
        if (basicInfoEl) {
          const rect = basicInfoEl.getBoundingClientRect()
          // 检查元素是否在容器的可视区域内
          this.isBasicInfoVisible = rect.top >= containerRect.top && rect.top < containerRect.bottom
        }
        
        if (collectionInfoEl) {
          const rect = collectionInfoEl.getBoundingClientRect()
          this.isCollectionInfoVisible = rect.top >= containerRect.top && rect.top < containerRect.bottom
        }
        
        if (sourceContentEl) {
          const rect = sourceContentEl.getBoundingClientRect()
          this.isSourceContentVisible = rect.top >= containerRect.top && rect.top < containerRect.bottom
        }
      })
    },
    handleScroll() {
      this.checkSectionVisibility()
    },
    checkMobile() {
      this.isMobile = isMobileDevice()
    },
    scrollToSection(sectionId) {
      this.$nextTick(() => {
        const element = document.getElementById(sectionId)
        const scrollContainer = document.querySelector('.detail-content')
        
        if (element && scrollContainer) {
          // 获取元素相对于滚动容器的位置
          const containerRect = scrollContainer.getBoundingClientRect()
          const elementRect = element.getBoundingClientRect()
          
          // 计算目标滚动位置（元素顶部相对于容器的位置，减去一些偏移量）
          const offset = 16 // 顶部偏移量
          const targetScrollTop = scrollContainer.scrollTop + (elementRect.top - containerRect.top) - offset
          
          scrollContainer.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          })
        }
      })
    },
    // 判断字段类型是否为 list
    isListType(key) {
      const column = this.columns.find(col => col.key === key)
      return column && column.type === 'list'
    },
    // 获取列表类型的所有元素数组
    getListItems(value) {
      if (!value || value === '-') {
        return []
      }
      // 如果是数组，直接返回
      if (Array.isArray(value)) {
        return value.map(item => String(item)).filter(item => item)
      }
      // 如果是字符串，尝试按逗号或分号分割
      if (typeof value === 'string') {
        return value.split(/[,;]/).map(item => item.trim()).filter(item => item)
      }
      return [String(value)]
    },
    isLinkField(key) {
      // 判断字段是否为链接字段
      const linkFields = ['source_link', 'resource_link', 'link']
      return linkFields.includes(key)
    }
  }
}
</script>

<style scoped>
.detail-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  max-width: 100vw;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.translate-button {
  color: rgba(0, 0, 0, 0.65);
  padding: 0 8px;
}

.translate-button:hover {
  color: #1890ff;
  background: #f0f5ff;
}

.translate-error {
  margin-bottom: 12px;
  color: #ff4d4f;
  font-size: 13px;
}

.detail-header-fixed {
  position: sticky;
  top: 0;
  z-index: 10;
  margin-bottom: 16px;
}

.detail-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.back-button {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 8px;
  color: rgba(0, 0, 0, 0.85);
  background: transparent;
  border: none;
}

.back-button:hover {
  background: #fafafa;
  color: rgba(0, 0, 0, 0.85);
}

.back-icon {
  width: 16px;
  height: 16px;
}

.data-source-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  font-size: 14px;
}

.badge-icon {
  width: 14px;
  height: 14px;
}

.title-section {
  background: linear-gradient(to right, #e6f7ff, #fff);
  border: 1px solid #91d5ff;
  border-radius: 4px;
  padding: 24px;
}

.title-text {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.title-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.info-card {
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.info-card :deep(.ant-card-head) {
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  padding: 12px 24px;
}

.info-card :deep(.ant-card-body) {
  padding: 16px 24px;
}

.card-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.info-content {
  width: 100%;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px 24px;
}

.info-item {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: flex-start;
}

.info-label {
  color: rgba(0, 0, 0, 0.45);
  min-width: 80px;
  max-width: 80px;
  font-size: 14px;
  flex-shrink: 0;
  line-height: 1.5;
}

.info-value {
  color: rgba(0, 0, 0, 0.85);
  flex: 1;
  font-size: 14px;
  word-break: break-word;
  line-height: 1.5;
}

.inline-translate-button {
  margin-left: 8px;
  padding: 0;
  height: auto;
}

.link-cell {
  color: #1890ff;
  text-decoration: none;
  word-break: break-all;
}

.link-cell:hover {
  color: #40a9ff;
  text-decoration: underline;
}

.collection-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.collection-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  background: #fafafa;
  transition: all 0.3s;
}

.collection-item:hover {
  border-color: #91d5ff;
  background: #e6f7ff;
}

.collection-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #1890ff;
  flex-shrink: 0;
}

.collection-label {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
}

.collection-value {
  color: rgba(0, 0, 0, 0.85);
  font-size: 14px;
}

/* Source Content Styles */
.source-content-card {
  margin-bottom: 16px;
}

.card-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.fullscreen-button {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.65);
  padding: 0 8px;
}

.fullscreen-button:hover {
  color: #1890ff;
  background: #f0f5ff;
}

.source-content-wrapper {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: auto;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px;
}

.source-content-text {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 12px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.85);
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  max-width: 100%;
  overflow-wrap: break-word;
}

/* Markdown 内容样式 */
.markdown-content {
  font-size: 14px;
  line-height: 1.8;
  color: rgba(0, 0, 0, 0.85);
  word-wrap: break-word;
}

.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.85);
}

.markdown-content :deep(h1) {
  font-size: 24px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 8px;
}

.markdown-content :deep(h2) {
  font-size: 20px;
  border-bottom: 1px solid #e8e8e8;
  padding-bottom: 8px;
}

.markdown-content :deep(h3) {
  font-size: 18px;
}

.markdown-content :deep(h4) {
  font-size: 16px;
}

.markdown-content :deep(p) {
  margin-bottom: 16px;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin-bottom: 16px;
  padding-left: 24px;
}

.markdown-content :deep(li) {
  margin-bottom: 8px;
}

.markdown-content :deep(blockquote) {
  margin: 16px 0;
  padding: 12px 16px;
  border-left: 4px solid #1890ff;
  background: #f0f5ff;
  color: rgba(0, 0, 0, 0.65);
}

.markdown-content :deep(code) {
  padding: 2px 6px;
  background: #f5f5f5;
  border-radius: 3px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 13px;
  color: #e83e8c;
}

.markdown-content :deep(pre) {
  margin: 16px 0;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 4px;
  overflow-x: auto;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 13px;
  line-height: 1.6;
}

.markdown-content :deep(pre code) {
  padding: 0;
  background: transparent;
  color: rgba(0, 0, 0, 0.85);
}

.markdown-content :deep(a) {
  color: #1890ff;
  text-decoration: none;
}

.markdown-content :deep(a:hover) {
  text-decoration: underline;
}

.markdown-content :deep(table) {
  width: 100%;
  margin: 16px 0;
  border-collapse: collapse;
}

.markdown-content :deep(table th),
.markdown-content :deep(table td) {
  padding: 8px 12px;
  border: 1px solid #e8e8e8;
}

.markdown-content :deep(table th) {
  background: #fafafa;
  font-weight: 600;
}

.markdown-content :deep(hr) {
  margin: 24px 0;
  border: none;
  border-top: 1px solid #e8e8e8;
}

.markdown-content :deep(img) {
  max-width: 100%;
  height: auto;
  margin: 16px 0;
  border-radius: 4px;
}

/* Floating Anchors */
.floating-anchors {
  position: fixed;
  right: 24px;
  bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
}

.anchor-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 100px;
}

.anchor-item:hover {
  background: #f0f5ff;
  border-color: #1890ff;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.2);
  transform: translateX(-4px);
}

.anchor-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1890ff;
  flex-shrink: 0;
  box-shadow: 0 0 4px rgba(24, 144, 255, 0.4);
}

.anchor-item:hover .anchor-dot {
  background: #40a9ff;
  box-shadow: 0 0 6px rgba(24, 144, 255, 0.6);
}

.anchor-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
  white-space: nowrap;
}

.anchor-item:hover .anchor-label {
  color: #1890ff;
}

/* Source Content Empty State */
.source-content-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 40px;
}

/* Fullscreen Styles */
body.fullscreen-open {
  overflow: hidden !important;
}

html.fullscreen-open {
  overflow: hidden !important;
}

.fullscreen-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 2000;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.fullscreen-header {
  flex-shrink: 0;
  height: 55px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  display: flex;
  align-items: center;
  padding: 0 24px;
}

.fullscreen-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.fullscreen-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.exit-fullscreen-button {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.65);
}

.exit-fullscreen-button:hover {
  color: #1890ff;
  background: #f0f5ff;
}

.fullscreen-content-wrapper {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: auto;
  background: #fafafa;
  padding: 24px;
}

.fullscreen-content-text {
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
  font-size: 14px;
  line-height: 1.8;
  color: rgba(0, 0, 0, 0.85);
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
  max-width: 100%;
  overflow-wrap: break-word;
}

.empty-text {
  color: rgba(0, 0, 0, 0.45);
  font-size: 14px;
}

.fullscreen-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.exit-fullscreen-button {
  display: flex;
  align-items: center;
  gap: 4px;
  color: rgba(0, 0, 0, 0.65);
}

.exit-fullscreen-button:hover {
  color: #1890ff;
  background: #f0f5ff;
}

/* 移动端优化 */
@media (max-width: 767px) {
  .detail-page {
    padding: 0;
    background: #f5f5f5;
  }

  .detail-header-fixed {
    position: sticky;
    top: 0;
    z-index: 1000;
    padding: 12px 16px;
    height: auto;
    min-height: 48px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 0;
  }

  .back-button {
    padding: 8px 12px;
    font-size: 14px;
    height: auto;
  }

  .back-icon {
    margin-right: 4px;
  }

  .data-source-badge {
    font-size: 12px;
    padding: 4px 8px;
    margin-left: 8px;
  }

  .detail-content {
    padding: 16px;
    gap: 12px;
  }

  .title-section {
    margin-bottom: 12px;
    padding: 16px;
    border-radius: 8px;
  }

  .title-text {
    font-size: 18px;
    line-height: 1.5;
    margin: 0;
    word-break: break-word;
  }

  .info-card {
    margin-bottom: 12px;
    border-radius: 8px;
  }

  .info-card :deep(.ant-card-head) {
    padding: 12px 16px;
    min-height: 48px;
  }

  .info-card :deep(.ant-card-body) {
    padding: 16px;
  }

  .card-title {
    font-size: 15px;
    font-weight: 600;
  }

  .info-grid {
    display: block;
    gap: 0;
  }

  .info-item {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 10px;
    padding: 0;
    gap: 8px;
  }

  .info-item:last-child {
    margin-bottom: 0;
  }

  .info-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    font-weight: 500;
    min-width: 70px;
    max-width: 70px;
    flex-shrink: 0;
    word-break: break-word;
    line-height: 1.5;
  }

  .info-value {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.85);
    flex: 1;
    word-break: break-word;
    line-height: 1.5;
  }

  .collection-content {
    display: block;
    gap: 0;
  }

  .collection-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    border-radius: 0;
    background: transparent;
    border-left: none;
    border-right: none;
    border-top: none;
  }

  .collection-item:last-child {
    margin-bottom: 0;
    border-bottom: none;
  }

  .collection-item:hover {
    background: transparent;
    border-color: #f0f0f0;
  }

  .collection-dot {
    width: 6px;
    height: 6px;
    margin-top: 6px;
    margin-right: 8px;
    flex-shrink: 0;
  }

  .collection-label {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
    margin-right: 4px;
    flex-shrink: 0;
  }

  .collection-value {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.85);
    word-break: break-word;
    flex: 1;
  }

  .source-content-wrapper {
    max-height: 50vh;
    padding: 12px;
    border-radius: 6px;
  }

  .source-content-text {
    font-size: 12px;
    line-height: 1.6;
  }

  .markdown-content {
    font-size: 14px;
    line-height: 1.7;
  }

  .markdown-content :deep(h1) {
    font-size: 20px;
    margin-top: 20px;
    margin-bottom: 12px;
  }

  .markdown-content :deep(h2) {
    font-size: 18px;
    margin-top: 18px;
    margin-bottom: 10px;
  }

  .markdown-content :deep(h3) {
    font-size: 16px;
    margin-top: 16px;
    margin-bottom: 8px;
  }

  .markdown-content :deep(p) {
    margin-bottom: 12px;
  }

  .fullscreen-button {
    font-size: 13px;
    padding: 4px 8px;
  }

  .floating-anchors {
    position: fixed;
    right: 12px;
    bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 1000;
  }

  .anchor-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    cursor: pointer;
    transition: all 0.3s ease;
    min-width: auto;
    font-size: 12px;
  }

  .anchor-item:active {
    background: #f0f5ff;
    border-color: #1890ff;
    transform: scale(0.95);
  }

  .anchor-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #1890ff;
    flex-shrink: 0;
  }

  .anchor-label {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.85);
    font-weight: 500;
    white-space: nowrap;
  }

  .fullscreen-container {
    padding: 12px;
  }

  .fullscreen-header {
    padding: 12px 16px;
    height: auto;
    min-height: 48px;
  }

  .fullscreen-title {
    font-size: 16px;
  }

  .fullscreen-content-wrapper {
    padding: 12px;
  }

  .fullscreen-content-text {
    font-size: 13px;
    line-height: 1.6;
  }
}
</style>

